"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Search, X, MessageSquare } from "lucide-react";
import { API_BASE_URL } from "@/lib/config";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

interface SearchResult {
  session_id: string;
  title: string;
  mode: string;
  updated_at: string;
  is_favorited: boolean;
  score: number;
  category?: string;
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function SearchDialog() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { user } = useAuth();
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const doSearch = useCallback(
    async (q: string) => {
      if (!q.trim() || !user?.uid) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/sessions/search?user_id=${encodeURIComponent(user.uid)}&q=${encodeURIComponent(q)}&limit=10`
        );
        if (!res.ok) throw new Error("Search failed");
        const data = await res.json();
        setResults(data.sessions || []);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [user?.uid]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  const navigateToResult = useCallback(
    (sessionId: string) => {
      setOpen(false);
      setQuery("");
      setResults([]);
      router.push(`/chat/${sessionId}`);
    },
    [router]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      navigateToResult(results[selectedIndex].session_id);
    }
  };

  return (
    <>
      <button
        type="button"
        data-tour="sidebar-search"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      >
        <Search size={14} />
        <span className="flex-1 text-left">Search sessions...</span>
        <kbd className="hidden md:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="top-[20%] translate-y-0 p-0 gap-0 max-w-lg overflow-hidden [&>button:last-child]:hidden">
          <VisuallyHidden.Root>
            <DialogTitle>Search sessions</DialogTitle>
          </VisuallyHidden.Root>
          <div className="flex items-center gap-2 border-b px-3">
            <Search size={16} className="shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search across all your sessions..."
              className="flex-1 h-11 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                Searching...
              </div>
            )}

            {!loading && query && results.length === 0 && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                No sessions found
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="py-1">
                {results.map((result, i) => (
                  <button
                    key={result.session_id}
                    onClick={() => navigateToResult(result.session_id)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                      i === selectedIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50"
                    }`}
                  >
                    <MessageSquare
                      size={16}
                      className="mt-0.5 shrink-0 text-muted-foreground"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium">
                        {result.title || "Untitled"}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {result.category && (
                          <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {result.category}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {result.updated_at ? timeAgo(result.updated_at) : ""}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!query && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                Type to search your sessions
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
